interface PaginationProps {
  currentPage: number
  totalPages: number
  maxButtons?: number
  onPageChange: (page: number) => void
}

export default function Pagination({
  currentPage,
  totalPages,
  maxButtons = 5,
  onPageChange,
}: Readonly<PaginationProps>) {
  if (totalPages <= 0) return null

  const currentBlock = Math.ceil(currentPage / maxButtons)
  const startPage = (currentBlock - 1) * maxButtons + 1
  const endPage = Math.min(startPage + maxButtons - 1, totalPages)
  const pages = Array.from(
    { length: Math.max(0, endPage - startPage + 1) },
    (_, index) => startPage + index,
  )

  function goPage(page: number) {
    if (page < 1 || page > totalPages || page === currentPage) return
    onPageChange(page)
  }

  function onClickPrev() {
    goPage(currentPage - 1)
  }

  function onClickNext() {
    goPage(currentPage + 1)
  }

  const navButtonClass =
    "focus-ring app-txt-body2 flex size-9 cursor-pointer items-center justify-center border border-app-secondary-500 bg-app-secondary-100 text-app-text-300 transition hover:bg-app-secondary-300 disabled:cursor-auto disabled:opacity-40"

  return (
    <nav
      aria-label="페이지네이션"
      className="flex items-center justify-center gap-1.5"
    >
      <button
        type="button"
        onClick={onClickPrev}
        disabled={currentPage === 1}
        aria-label="이전 페이지로"
        className={navButtonClass}
      >
        ‹
      </button>

      {pages.map((page) => {
        const isActive = page === currentPage

        return (
          <button
            key={page}
            type="button"
            onClick={() => goPage(page)}
            disabled={isActive}
            aria-current={isActive ? "page" : undefined}
            aria-label={isActive ? `${page}페이지 (현재)` : `${page}페이지`}
            className={`focus-ring app-txt-body2 flex size-9 items-center justify-center border transition ${
              isActive
                ? "border-app-primary-500 bg-app-primary-500 font-bold text-app-text-100"
                : "cursor-pointer border-app-secondary-500 bg-app-secondary-100 text-app-text-300 hover:bg-app-secondary-300"
            }`}
          >
            {page}
          </button>
        )
      })}

      <button
        type="button"
        onClick={onClickNext}
        disabled={currentPage === totalPages}
        aria-label="다음 페이지로"
        className={navButtonClass}
      >
        ›
      </button>
    </nav>
  )
}
